import { type ReactElement, type ReactNode } from 'react';
import { IconButton } from './primitives';

/**
 * The anatomy of the right-hand record drawer (V-LIB-SHELL, D509; audit admin #8). The Admin cloned
 * `padding: '20px 24px'` for the header and the body 29 times across 13 files; this is the one version, on the token
 * padding (`--space-5` block, `--space-6` inline — the same 20/24 the clones drew, now on the scale). Put them inside
 * `<Drawer>`:
 *
 *   <Drawer open onClose={close} labelledBy={titleId}>
 *     <DrawerHeader titleId={titleId} title={name} subtitle={ref} onClose={close} closeLabel={t('common.close')} actions={…} />
 *     <DrawerBody>
 *       <DrawerSection title={t('ride.route')}>…</DrawerSection>
 *     </DrawerBody>
 *     <DrawerFooter><Button variant="primary">Save</Button></DrawerFooter>
 *   </Drawer>
 *
 * The body scrolls on its own (the header and footer stay put), so a long record never pushes the actions off screen.
 */

/** The drawer's head: title (and subtitle), optional header actions and the close control, under a hairline. */
export function DrawerHeader({ title, subtitle, titleId, actions, onClose, closeLabel, children }: { title: ReactNode; subtitle?: ReactNode; /** Pair with `<Drawer labelledBy>`. */ titleId?: string; actions?: ReactNode; onClose?: () => void; /** The close control's accessible name; required with `onClose`. */ closeLabel?: string; /** Extra content under the title row (a status badge strip, tabs…). */ children?: ReactNode }): ReactElement {
  return (
    <header className="vello-drawer__header">
      <div className="vello-drawer__header-row">
        <div className="vello-drawer__header-text">
          <h2 id={titleId} className="vello-drawer__title">
            {title}
          </h2>
          {subtitle !== undefined && <p className="vello-drawer__subtitle">{subtitle}</p>}
        </div>
        {actions !== undefined && <div className="vello-drawer__header-actions">{actions}</div>}
        {onClose !== undefined && <IconButton icon="x" label={closeLabel ?? ''} onClick={onClose} data-testid="drawer-close" />}
      </div>
      {children}
    </header>
  );
}

/** The drawer's scrolling content area. `flush` drops the padding for a body that draws its own (a table, tabs). */
export function DrawerBody({ children, flush = false }: { children: ReactNode; flush?: boolean }): ReactElement {
  return (
    <div className="vello-drawer__body" data-flush={flush ? 'true' : undefined}>
      {children}
    </div>
  );
}

/** A titled block inside the body: a `micro`-role heading and its content, separated from the next block by a hairline. */
export function DrawerSection({ title, children }: { title?: string; children: ReactNode }): ReactElement {
  return (
    <section className="vello-drawer__section">
      {title !== undefined && <h3 className="vello-drawer__section-title">{title}</h3>}
      {children}
    </section>
  );
}

/** The drawer's action bar (the existing `vello-drawer__foot`): stays at the bottom, one primary at most. */
export function DrawerFooter({ children }: { children: ReactNode }): ReactElement {
  return <footer className="vello-drawer__foot">{children}</footer>;
}
